<script setup lang="ts">
import { ref, computed } from 'vue';
import SharedAutocomplete from 'src/components/shared/SharedAutocomplete.vue';
import { useTicketService } from 'src/services/ticket-service';
import { useNotification } from 'src/composables/useNotification';
import { useAssigneeService } from 'src/services/assignee-service';
import { CreateActionLabourDTOType, CreateAssignmentDTOType } from 'src/api/models';
import type { FetchActionResponseLite, FetchAssigneeResponse } from 'src/api/models';
import { postFetchAllAbstractActions } from 'src/api/queries/order-portal-bff';
import { formatCurrency } from 'src/utils/currency';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{
  ticketId: string;
  ticketAssignee?: { id: string; name: string; function?: string; price_per_hour?: number } | null;
}>();

const emit = defineEmits<{
  'assignment-added': [];
  'close': [];
}>();

const { addAssignmentToTicket } = useTicketService();
const { showSuccess, showError } = useNotification();
const { searchAssignees } = useAssigneeService();

const isOffer = ref(false);
const isSubmitting = ref(false);

const selectedAction = ref<FetchActionResponseLite | null>(null);
const actionFetchFn = (query: string, page: number) => postFetchAllAbstractActions(
  { name: query || undefined },
  { page, limit: 50 },
);

const mechInput = ref<FetchAssigneeResponse | null>(null);
const mechFetchFn = (query: string, page: number) => searchAssignees(query || null, page, 50);

const offerMechanic = ref<FetchAssigneeResponse | null>(
  props.ticketAssignee ? { id: props.ticketAssignee.id, name: props.ticketAssignee.name } as FetchAssigneeResponse : null
);
const offerHours = ref(1);

interface LaborEntry {
  id: number;
  assignee: Record<string, any>;
  hours: number;
  type: CreateActionLabourDTOType;
}

const laborEntries = ref<LaborEntry[]>([]);
const entryIdCounter = ref(1);

const isFormValid = computed(() => {
  if (!selectedAction.value?.id) return false;
  if (isOffer.value) return !!offerMechanic.value?.id && offerHours.value > 0;
  return true;
});

function handleActionSelect(event: { value: FetchActionResponseLite }) {
  selectedAction.value = event.value;
}

function handleActionClear() {
  selectedAction.value = null;
  laborEntries.value = [];
}

function handleMechSelect(event: { value: FetchAssigneeResponse }) {
  laborEntries.value.push({
    id: entryIdCounter.value++,
    assignee: event.value,
    hours: 1,
    type: CreateActionLabourDTOType.variable,
  });
  mechInput.value = null;
}

function handleOfferMechSelect(event: { value: FetchAssigneeResponse }) {
  offerMechanic.value = event.value;
}

function handleOfferMechClear() {
  offerMechanic.value = null;
}

function removeLaborEntry(entryId: number) {
  laborEntries.value = laborEntries.value.filter((e) => e.id !== entryId);
}

function toggleLaborType(entry: LaborEntry) {
  entry.type = entry.type === CreateActionLabourDTOType.variable
    ? CreateActionLabourDTOType.fixed
    : CreateActionLabourDTOType.variable;
}

function resetForm() {
  selectedAction.value = null;
  laborEntries.value = [];
  entryIdCounter.value = 1;
  offerMechanic.value = props.ticketAssignee
    ? { id: props.ticketAssignee.id, name: props.ticketAssignee.name } as FetchAssigneeResponse
    : null;
  offerHours.value = 1;
  isOffer.value = false;
}

async function handleSubmit() {
  if (!isFormValid.value || !props.ticketId || !selectedAction.value?.id) return;

  isSubmitting.value = true;
  try {
    const assignmentType = isOffer.value
      ? CreateAssignmentDTOType.fixed
      : CreateAssignmentDTOType.variable;

    let entries: Array<{ assignee_id: string; labor_hours: number; labor_type: CreateActionLabourDTOType }> = [];

    if (isOffer.value && offerMechanic.value?.id) {
      entries = [{
        assignee_id: offerMechanic.value.id,
        labor_hours: offerHours.value,
        labor_type: CreateActionLabourDTOType.fixed,
      }];
    } else {
      entries = laborEntries.value
        .filter((e) => e.assignee?.id && e.hours > 0)
        .map((e) => ({
          assignee_id: e.assignee.id,
          labor_hours: e.hours,
          labor_type: e.type,
        }));
    }

    const result = await addAssignmentToTicket(props.ticketId, {
      action_id: selectedAction.value.id,
      laborEntries: entries.length > 0 ? entries : undefined,
      type: assignmentType,
    });

    if (result) {
      showSuccess({ message: 'Assignment added' });
      emit('assignment-added');
      resetForm();
    } else {
      showError({ message: 'Failed to add assignment' });
    }
  } catch (error) {
    console.error('Error adding assignment:', error);
    showError({ message: 'Failed to add assignment' });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <aside class="panel">
    <div class="panel-scroll">
      <div class="p-5">
        <TicketEditSectionHeader title="Action" />

        <SharedAutocomplete
          v-model="selectedAction"
          :fetch-fn="actionFetchFn"
          placeholder="Search action..."
          @item-select="handleActionSelect"
          @clear="handleActionClear"
        >
          <template #option="{ option }">
            <div class="ac-option">
              <span class="ac-option-name">{{ option.name }}</span>
              <span v-if="option.action_type" class="ac-option-sub">{{ option.action_type }}</span>
            </div>
          </template>
          <template #detail="{ item }">
            <div v-if="item.name" class="ac-detail-name">{{ item.name }}</div>
            <div v-if="item.action_type" class="ac-detail-field">
              <span class="ac-detail-label">Action type</span>
              <span class="ac-detail-value">{{ item.action_type }}</span>
            </div>
            <div v-if="item.type" class="ac-detail-field">
              <span class="ac-detail-label">Type</span>
              <span class="ac-detail-value">{{ item.type }}</span>
            </div>
            <div v-if="item.price != null" class="ac-detail-field">
              <span class="ac-detail-label">Price</span>
              <span class="ac-detail-value">{{ formatCurrency(item.price) }}</span>
            </div>
            <div v-if="item.status" class="ac-detail-field">
              <span class="ac-detail-label">Status</span>
              <span class="ac-detail-value">{{ item.status }}</span>
            </div>
          </template>
        </SharedAutocomplete>
      </div>

      <div class="divider" />

      <div class="p-5">
        <TicketEditSectionHeader title="Type" />
        <div class="type-row">
          <button class="type-opt" :class="{ 'type-opt-active': !isOffer }" @click="isOffer = false">
            <i class="pi pi-wrench" style="font-size: 11px" />
            Work
          </button>
          <button class="type-opt" :class="{ 'type-opt-active': isOffer }" @click="isOffer = true">
            <i class="pi pi-file" style="font-size: 11px" />
            Offer
          </button>
        </div>
      </div>

      <div class="divider" />

      <div class="p-5">
        <TicketEditSectionHeader :title="isOffer ? 'Pricing' : 'Mechanics'" />

        <!-- Offer mode -->
        <template v-if="isOffer">
          <SharedAutocomplete
            v-model="offerMechanic"
            :fetch-fn="mechFetchFn"
            placeholder="Select mechanic..."
            class="mb-3"
            @item-select="handleOfferMechSelect"
            @clear="handleOfferMechClear"
          />

          <div class="prop-row">
            <span class="prop-label">Hours</span>
            <div class="stepper">
              <button @click="offerHours = Math.max(0.5, +(offerHours - 0.5).toFixed(1))"><i class="pi pi-minus" style="font-size: 9px" /></button>
              <span>{{ offerHours }}h</span>
              <button @click="offerHours = +(offerHours + 0.5).toFixed(1)"><i class="pi pi-plus" style="font-size: 9px" /></button>
            </div>
          </div>
        </template>

        <!-- Work mode -->
        <template v-else>
          <div v-for="entry in laborEntries" :key="entry.id" class="mech-entry">
            <div class="entity-header">
              <div class="entity-icon icon-green">
                <i class="pi pi-user" style="font-size: 11px" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="entity-name">{{ entry.assignee.name }}</div>
              </div>
              <button class="entity-clear" @click="removeLaborEntry(entry.id)">
                <i class="pi pi-times" style="font-size: 9px" />
              </button>
            </div>
            <div class="mech-props">
              <div class="prop-row" @click="toggleLaborType(entry)">
                <span class="prop-label">Type</span>
                <span class="prop-val" :class="entry.type === 'variable' ? 'text-blue-500' : 'text-amber-500'">
                  {{ entry.type }}
                  <i class="pi pi-chevron-down prop-chevron" />
                </span>
              </div>
              <div class="prop-row">
                <span class="prop-label">Hours</span>
                <div class="stepper">
                  <button @click="entry.hours > 0.5 && (entry.hours = +(entry.hours - 0.5).toFixed(1))"><i class="pi pi-minus" style="font-size: 9px" /></button>
                  <span>{{ entry.hours }}h</span>
                  <button @click="entry.hours = +(entry.hours + 0.5).toFixed(1)"><i class="pi pi-plus" style="font-size: 9px" /></button>
                </div>
              </div>
            </div>
          </div>

          <SharedAutocomplete
            v-model="mechInput"
            :fetch-fn="mechFetchFn"
            placeholder="Add mechanic..."
            :show-clear="false"
            class="mt-2"
            @item-select="handleMechSelect"
          />
        </template>
      </div>
    </div>

    <div class="panel-footer">
      <button class="cancel-btn" @click="emit('close')">Cancel</button>
      <button
        class="submit-btn"
        :disabled="!isFormValid || isSubmitting"
        @click="handleSubmit"
      >
        <i v-if="isSubmitting" class="pi pi-spinner pi-spin" style="font-size: 12px" />
        {{ isOffer ? 'Add to Offer' : 'Add Assignment' }}
      </button>
    </div>
  </aside>
</template>

<style scoped>
.entity-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.entity-icon {
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icon-green {
  background: color-mix(in srgb, var(--p-green-500) 12%, transparent);
  color: var(--p-green-500);
}

.entity-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--p-surface-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:root[class*='app-dark'] .entity-name {
  color: var(--p-surface-100);
}

.entity-clear {
  width: 1.375rem;
  height: 1.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  color: var(--p-surface-400);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
.entity-clear:hover {
  color: var(--p-red-500);
  background: var(--p-red-50);
}
:root[class*='app-dark'] .entity-clear:hover {
  color: var(--p-red-400);
  background: color-mix(in srgb, var(--p-red-500) 12%, transparent);
}

.mech-entry {
  border: 1px solid var(--p-surface-200);
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
  background: var(--p-surface-0);
  margin-bottom: 0.5rem;
}
:root[class*='app-dark'] .mech-entry {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
}

.mech-props {
  margin-top: 0.375rem;
  padding-top: 0.375rem;
  border-top: 1px solid var(--p-surface-100);
}
:root[class*='app-dark'] .mech-props {
  border-top-color: var(--p-surface-700);
}

.prop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.125rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background 0.15s;
}
.prop-row:hover {
  background: var(--p-surface-100);
}
:root[class*='app-dark'] .prop-row:hover {
  background: color-mix(in srgb, var(--p-surface-700) 50%, transparent);
}
.prop-label {
  font-size: 0.875rem;
  color: var(--p-surface-500);
}
.prop-val {
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.prop-chevron {
  font-size: 8px;
  opacity: 0;
  transition: opacity 0.15s;
}
.prop-row:hover .prop-chevron {
  opacity: 0.6;
}

.cancel-btn {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-surface-400);
  cursor: pointer;
  transition: color 0.15s;
}
.cancel-btn:hover { color: var(--p-surface-600); }
:root[class*='app-dark'] .cancel-btn:hover { color: var(--p-surface-300); }
</style>
